import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import type { ApiKeyCreatedOut, ApiKeyExtendIn, ApiKeyIn, ApiKeyListOut, ApiKeyOut } from '../generated/schemas';
import { formatUtcDate } from '@/ui/format';
import { queryKeys } from './keys';

/** The person's API keys (newest first, expired ones flagged, revoked ones gone): the secret is never listed. */
export function useApiKeys() {
  return useQuery({ queryKey: queryKeys.apiKeys, queryFn: () => api<ApiKeyListOut>('/api/api-keys') });
}

/** The answer carries the key itself, once: never cached, and the dialog keeps it only while it is open. */
export function useCreateApiKey() {
  const client = useQueryClient();
  return useMutation({
    gcTime: 0,
    meta: { quiet: true },
    mutationFn: (body: ApiKeyIn) => api<ApiKeyCreatedOut>('/api/api-keys', 'POST', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.apiKeys }),
  });
}

export function useExtendApiKey() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: (key: ApiKeyOut) => `Key extended to ${formatUtcDate(key.expires_at)} UTC` },
    mutationFn: ({ id, ...body }: ApiKeyExtendIn & { id: string }) => api<ApiKeyOut>(`/api/api-keys/${id}/extend`, 'POST', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.apiKeys }),
  });
}

export function useRevokeApiKey() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: 'Key revoked' },
    mutationFn: (id: string) => api<void>(`/api/api-keys/${id}`, 'DELETE'),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.apiKeys }),
  });
}

/** The integration API's OpenAPI document: built once on the server, so it is kept for the whole visit. */
export function useIntegrationOpenapi() {
  return useQuery({
    queryKey: queryKeys.integrationOpenapi,
    queryFn: () => api<IntegrationDocument>('/api/integration/openapi.json'),
    staleTime: Infinity,
  });
}

/** The parts of an OpenAPI 3.1 document the reference shows. */
export interface IntegrationDocument {
  info: { title: string; version: string; description?: string };
  paths: Record<string, Record<string, IntegrationOperation>>;
  components: { schemas: Record<string, JsonSchema> };
}

export interface IntegrationOperation {
  operationId: string;
  summary: string;
  description: string;
  tags?: string[];
  parameters?: { name: string; in: string; required?: boolean; schema?: JsonSchema }[];
  requestBody?: { content: Record<string, { schema: JsonSchema }> };
  responses: Record<string, { description: string; content?: Record<string, { schema: JsonSchema }> }>;
}

export interface JsonSchema {
  $ref?: string;
  type?: string | string[];
  format?: string;
  enum?: unknown[];
  items?: JsonSchema;
  anyOf?: JsonSchema[];
  properties?: Record<string, JsonSchema>;
  required?: string[];
  maxLength?: number;
  description?: string;
}
